import {zodResolver} from "@hookform/resolvers/zod";
import {Brand, useCurrentCompany, useUpdateBeforePayment} from "@mytour/platform";
import type {UpdateBeforePaymentPayload} from "@mytour/shared";
import {Loading} from "@mytour/ui/Loading";
import {Splitter} from "@mytour/ui/layout";
import {useEffect} from "react";
import {useForm} from "react-hook-form";
import {Trans as Translate, useTranslation} from "react-i18next";
import {
  Button,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";
import {z} from "zod";

const paymentDataSchema = z.object({
  company: z.string().min(1, "Champ obligatoire"),
  address: z.string().min(1, "Champ obligatoire"),
  address2: z.string(),
  zip: z.string().regex(/^[0-9]{5}$/, "Le code postal en France est une suite de cinq chiffres"),
  city: z.string().min(1, "Champ obligatoire"),
  phone: z.string(),
  email: z.string().min(5, "Email trop court").max(254, "Email trop long").email("Email invalide"),
});

type PaymentDataFormData = z.infer<typeof paymentDataSchema>;

export function PaymentDataModal({
  onAbort,
  onValidate,
}: {
  onAbort: () => void;
  onValidate: () => void;
}) {
  const {company, isCompanyLoading} = useCurrentCompany();
  const {isUpdatingBeforePayment, updateBeforePayment} = useUpdateBeforePayment();

  const loading = isCompanyLoading;
  const updating = isUpdatingBeforePayment;

  const {t: translate} = useTranslation();

  const {
    register,
    handleSubmit,
    reset,
    formState: {errors, isDirty},
  } = useForm<PaymentDataFormData>({
    resolver: zodResolver(paymentDataSchema),
    defaultValues: {
      company: company?.legal.legalName ?? "",
      address: company?.legal.administrativeAddress.streetAddress ?? "",
      address2: company?.legal.administrativeAddress.streetAddress2 ?? "",
      zip: company?.legal.administrativeAddress.zipCode ?? "",
      city: company?.legal.administrativeAddress.city ?? "",
      phone: company?.phone ?? "",
      email: company?.email ?? "",
    },
  });

  useEffect(() => {
    if (!company) {
      return;
    }

    reset({
      company: company.legal.legalName ?? "",
      address: company.legal.administrativeAddress.streetAddress ?? "",
      address2: company.legal.administrativeAddress.streetAddress2 ?? "",
      zip: company.legal.administrativeAddress.zipCode ?? "",
      city: company.legal.administrativeAddress.city ?? "",
      phone: company.phone ?? "",
      email: company.email ?? "",
    });
  }, [company, reset]);

  return (
    <Modal isOpen={true} backdrop="static" autoFocus={false} centered>
      <ModalHeader close={<></>} className="mytour-header">
        <Brand />
        {"vérification de vos données"}
      </ModalHeader>
      <ModalBody>
        <p>
          <b>{"Merci de vérifier vos informations"}</b>
        </p>
        {loading ? (
          <Loading />
        ) : (
          <Form
            id="settings-form"
            onSubmit={handleSubmit(onSubmit)}
            style={{display: "flex", flexDirection: "column", flexGrow: 1}}
          >
            <div style={{flexGrow: 1}}>
              <FormGroup>
                <Label for="platform-user-company">
                  {translate("myTourApp.platformUser.company")} *
                </Label>
                <Input
                  className="form-control"
                  id="platform-user-company"
                  innerRef={register("company").ref}
                  onChange={register("company").onChange}
                  onBlur={register("company").onBlur}
                  name="company"
                  disabled={updating}
                  invalid={!!errors.company}
                />
                {errors.company && <FormFeedback>{errors.company.message}</FormFeedback>}
              </FormGroup>

              <FormGroup>
                <Label for="platform-user-address">
                  {translate("myTourApp.platformUser.address")} *
                </Label>
                <Input
                  className="form-control"
                  id="platform-user-address"
                  innerRef={register("address").ref}
                  onChange={register("address").onChange}
                  onBlur={register("address").onBlur}
                  name="address"
                  disabled={updating}
                  invalid={!!errors.address}
                />
                {errors.address && <FormFeedback>{errors.address.message}</FormFeedback>}
              </FormGroup>
              <FormGroup>
                <Input
                  className="form-control"
                  id="platform-user-address2"
                  innerRef={register("address2").ref}
                  onChange={register("address2").onChange}
                  onBlur={register("address2").onBlur}
                  name="address2"
                  disabled={updating}
                />
              </FormGroup>

              <Splitter>
                <FormGroup>
                  <Label for="platform-user-zip">
                    {translate("myTourApp.platformUser.zip")} *
                  </Label>
                  <Input
                    className="form-control"
                    id="platform-user-zip"
                    innerRef={register("zip").ref}
                    onChange={register("zip").onChange}
                    onBlur={register("zip").onBlur}
                    name="zip"
                    disabled={updating}
                    invalid={!!errors.zip}
                  />
                  {errors.zip && <FormFeedback>{errors.zip.message}</FormFeedback>}
                </FormGroup>
                <FormGroup>
                  <Label for="platform-user-city">
                    {translate("myTourApp.platformUser.city")} *
                  </Label>
                  <Input
                    className="form-control"
                    id="platform-user-city"
                    innerRef={register("city").ref}
                    onChange={register("city").onChange}
                    onBlur={register("city").onBlur}
                    name="city"
                    disabled={updating}
                    invalid={!!errors.city}
                  />
                  {errors.city && <FormFeedback>{errors.city.message}</FormFeedback>}
                </FormGroup>
              </Splitter>

              <FormGroup>
                <Label for="platform-user-phone">
                  {translate("myTourApp.platformUser.phone")}
                </Label>
                <Input
                  className="form-control"
                  id="platform-user-phone"
                  innerRef={register("phone").ref}
                  onChange={register("phone").onChange}
                  onBlur={register("phone").onBlur}
                  name="phone"
                  disabled={updating}
                />
              </FormGroup>

              <FormGroup>
                <Label for="platform-user-email">{translate("global.form.email.label")} *</Label>
                <Input
                  id="platform-user-email"
                  type="email"
                  placeholder={translate("global.form.email.placeholder")}
                  innerRef={register("email").ref}
                  onChange={register("email").onChange}
                  onBlur={register("email").onBlur}
                  name="email"
                  disabled={updating}
                  invalid={!!errors.email}
                />
                {errors.email && <FormFeedback>{errors.email.message}</FormFeedback>}
              </FormGroup>
            </div>
            {isDirty && (
              <Button color="primary" type="submit" disabled={updating}>
                <Translate i18nKey="settings.form.button">Save</Translate>
              </Button>
            )}
          </Form>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={onAbort} id="abort" disabled={updating}>
          Retour
        </Button>
        <Button
          onClick={onValidate}
          type="submit"
          color="primary"
          style={{float: "right"}}
          disabled={isDirty || updating}
        >
          {"Valider"}
        </Button>
      </ModalFooter>
    </Modal>
  );

  async function onSubmit(data: PaymentDataFormData) {
    const payload: UpdateBeforePaymentPayload = {
      legalName: data.company,
      legalAddress: data.address,
      legalAddress2: data.address2,
      legalCity: data.city,
      legalZip: data.zip,
      phone: data.phone,
      email: data.email,
    };
    await updateBeforePayment(payload);
    reset(data);
  }
}
